<template>
  <div class="main-container detail">
    <div v-if="memuat" class="loading-bar" role="progressbar" :aria-label="$t('common.loading')"></div>

    <!-- ══ Sampul ══ -->
    <section class="detail__hero">
      <div class="detail__hero-bg" aria-hidden="true"></div>

      <div class="app-container">
        <div class="detail__hero-grid">
          <!-- Poster turnamen, rasio potret mengikuti berkas aslinya -->
          <div class="detail__poster" v-reveal>
            <SmartImage
              :source="turnamen.image"
              :alt="turnamen.name"
              :label="turnamen.slug || turnamen.name"
              ratio="3 / 4"
            />
          </div>

          <div class="detail__intro" v-reveal>
            <span class="ui-badge" :class="`ui-badge--${status.tone}`">
              <i :class="status.ikon" aria-hidden="true"></i>
              {{ $t(status.kunci) }}
            </span>

            <h1 class="detail__title">{{ turnamen.name || '—' }}</h1>

            <ul class="detail__facts">
              <li v-if="turnamen.location">
                <i class="bi bi-geo-alt" aria-hidden="true"></i>
                <span><small>{{ $t('tournament.location') }}</small>{{ turnamen.location }}</span>
              </li>
              <li v-if="turnamen.event_date">
                <i class="bi bi-calendar-event" aria-hidden="true"></i>
                <span><small>{{ $t('tournament.date') }}</small>{{ turnamen.event_date }}</span>
              </li>
              <li v-if="turnamen.technical_meeting_date">
                <i class="bi bi-people" aria-hidden="true"></i>
                <span>
                  <small>{{ $t('tournament.technicalMeeting') }}</small>
                  {{ tanggalPanjang(turnamen.technical_meeting_date) }}
                </span>
              </li>
              <li v-if="turnamen.registration_deadline" :class="{ 'is-mendesak': pendaftaranMendesak }">
                <i class="bi bi-hourglass-split" aria-hidden="true"></i>
                <span>
                  <small>{{ $t('tournament.registrationDeadline') }}</small>
                  {{ tanggalPanjang(turnamen.registration_deadline) }}
                  <strong v-if="keteranganSisa" class="detail__sisa">{{ keteranganSisa }}</strong>
                </span>
              </li>
            </ul>

            <div class="detail__actions">
              <router-link
                v-if="status.bisaDaftar && turnamen.slug"
                :to="{ name: 'registration', params: { slug: turnamen.slug } }"
                class="ui-btn ui-btn--primary ui-btn--lg hover-lift"
              >
                <i class="bi bi-pencil-square" aria-hidden="true"></i>
                {{ $t('home.registerContingent') }}
              </router-link>

              <!--
                Tombol proposal hanya muncul kalau berkasnya memang PDF.
                Pernah terjadi kolom dokumen berisi gambar banner, sehingga
                menekan "Unduh Proposal" justru mengunduh poster acara — dan
                tidak ada yang mengira itu kesalahan sistem.
              -->
              <a
                v-if="adaProposal"
                :href="urlProposal"
                target="_blank"
                rel="noopener noreferrer"
                class="ui-btn ui-btn--secondary ui-btn--lg"
                :class="{
                  'is-downloading': mengunduh === 'loading',
                  'is-success': mengunduh === 'success',
                }"
                download
                @click="saatKlikUnduhProposal($event)"
              >
                <i v-if="mengunduh === 'loading'" class="bi bi-arrow-repeat ikon-putar text-danger" aria-hidden="true"></i>
                <i v-else-if="mengunduh === 'success'" class="bi bi-check-circle-fill text-success ikon-sukses" aria-hidden="true"></i>
                <i v-else class="bi bi-file-earmark-arrow-down" aria-hidden="true"></i>
                {{ mengunduh === 'loading' ? 'Memproses Unduhan...' : (mengunduh === 'success' ? 'Unduhan Dimulai!' : $t('tournament.downloadDocument')) }}
              </a>

              <a
                v-if="turnamen.whatsapp_group"
                :href="turnamen.whatsapp_group"
                target="_blank"
                rel="noopener noreferrer"
                class="ui-btn ui-btn--lg detail__wa"
              >
                <i class="bi bi-whatsapp" aria-hidden="true"></i>
                {{ $t('tournament.joinGroup') }}
              </a>

              <router-link to="/schedule" class="ui-btn ui-btn--ghost ui-btn--lg">
                <i class="bi bi-calendar3" aria-hidden="true"></i>
                {{ $t('home.ctaSchedule') }}
              </router-link>
            </div>

            <!--
              Ketika pendaftaran belum dibuka atau sudah selesai, tombol daftar
              dihilangkan. Tanpa keterangan ini pengunjung akan mengira
              halamannya rusak, bukan mengira pendaftarannya memang belum ada.
            -->
            <p v-if="!status.bisaDaftar && turnamen.slug" class="detail__note">
              <i class="bi bi-info-circle" aria-hidden="true"></i>
              {{ keteranganTutup }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- ══ Keterangan ══ -->
    <section v-if="turnamen.description && turnamen.description.trim() !== '-' && turnamen.description.trim() !== ''" class="section">
      <div class="app-container">
        <div class="ui-card" v-reveal>
          <div class="ui-card__body">
            <h2 class="detail__subtitle">{{ $t('tournament.description') }}</h2>
            <p class="detail__desc">{{ turnamen.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ══ Kategori, usia, kontak ══ -->
    <!-- Biaya & narahubung: milik acara ini, bukan milik situs.
         Ditaruh di sini, bukan di beranda, karena tiap acara punya
         biaya dan panitia yang berbeda. -->
    <section v-if="biayaKategori.length" class="section section--muted">
      <div class="app-container">
          <header class="section__head section__head--center" v-reveal>
            <h2 class="section-title">{{ $t('home.feeTitle') }}</h2>
            <p class="section-subtitle" style="margin-inline: auto">{{ $t('home.feeSubtitle') }}</p>
          </header>

          <div class="biaya" v-reveal>
            <div v-for="k in biayaKategori" :key="k.id" class="biaya__baris">
              <span class="biaya__nama">{{ k.name }}</span>
              <span class="biaya__nilai">{{ rupiah(k.registration_fee) }}</span>
            </div>
          </div>
        </div>
      </section>

      <section v-if="narahubung.length" class="section">
        <div class="app-container">
          <header class="section__head section__head--center" v-reveal>
            <h2 class="section-title">{{ $t('home.contactTitle') }}</h2>
            <p class="section-subtitle" style="margin-inline: auto">{{ $t('home.contactSubtitle') }}</p>
          </header>

          <div class="kontak-grid">
            <a
              v-for="n in narahubung"
              :key="n.id"
              :href="tautanWa(n.phone)"
              target="_blank"
              rel="noopener noreferrer"
              class="kontak ui-card"
            >
              <span class="kontak__ikon"><i class="bi bi-whatsapp" aria-hidden="true"></i></span>
              <span class="kontak__isi">
                <strong class="kontak__nama">{{ n.name }}</strong>
                <span v-if="n.description" class="kontak__peran">{{ n.description }}</span>
                <span class="kontak__telp">{{ n.phone }}</span>
              </span>
              <i class="bi bi-arrow-right kontak__panah" aria-hidden="true"></i>
            </a>
          </div>
        </div>
      </section>

      <TournamentInfo
        :tournament="{
          location: turnamen.location,
          event_date: turnamen.event_date,
          matchCategories: turnamen.categories || [],
          ageCategories: turnamen.age_categories || [],
          contactPersons: turnamen.contact_persons || [],
        }"
      />

      <OurActivity :activities="turnamen.activities || []" />
  </div>
</template>

<script>
import TournamentInfo from '@/components/main/TournamentInfo.vue';
import OurActivity from '@/components/main/OurActivity.vue';
import SmartImage from '@/components/ui/SmartImage.vue';
import { infoStatus, STATUS } from '@/utils/status';
import { mediaUrl } from '@/utils/media';
import http from '@/services/http';

export default {
  name: 'GalleryDetail',

  components: { TournamentInfo, OurActivity, SmartImage },

  data() {
    return {
      // Diekspos ke templat supaya perbandingan status tidak perlu menulis
      // ulang teks 'selesai' yang mudah salah ketik.
      STATUS,
      turnamen: {},
      memuat: false,
      mengunduh: false,
    };
  },

  computed: {
    /** Keterangan saat tombol daftar tidak ada, sesuai alasan tertutupnya. */
    keteranganTutup() {
      const k = this.status?.kunci;
      if (this.status?.status === STATUS.SELESAI) return this.$t('status.sudahSelesaiDesc');
      if (k === 'status.kuotaPenuh') return this.$t('status.kuotaPenuhDesc');
      if (k === 'status.ditutup' || this.status?.status === STATUS.DITUTUP) return this.$t('status.ditutupDesc');
      return this.$t('status.belumDibukaDesc');
    },
    status() {
      return infoStatus(this.turnamen);
    },

    /**
     * Berkas proposal hanya dianggap ada bila memang berupa PDF.
     *
     * Kolom `document` di basis data pernah terisi berkas gambar, sehingga
     * tombol "Unduh Proposal" mengunduh poster acara. Kesalahan seperti itu
     * tidak terlihat seperti kerusakan — berkasnya terunduh dengan sukses,
     * hanya isinya yang bukan yang dijanjikan. Backend kini menolak selain
     * PDF, dan penjagaan di sini menutup data lama yang terlanjur salah.
     */
    adaProposal() {
      const d = this.turnamen.document;
      return typeof d === 'string' && /\.pdf($|\?)/i.test(d.trim());
    },

    urlProposal() {
      return this.turnamen.document ? (mediaUrl(this.turnamen.document) || this.turnamen.document) : '#';
    },

    /**
     * Hitung mundur pendaftaran, dari angka yang DIHITUNG SERVER.
     * Jam komputer pengunjung bisa salah, dan "tersisa 2 hari" yang keliru
     * di halaman pendaftaran membuat orang telat mendaftar.
     */
    keteranganSisa() {
      // Sudah ditutup (admin / kuota penuh): hitung mundur tidak relevan.
      if (!this.status?.bisaDaftar) return '';
      const n = this.turnamen.sisa_hari_pendaftaran;
      if (n === null || n === undefined) return '';
      if (n < 0) return this.$t('tournament.deadlinePassed');
      if (n === 0) return this.$t('tournament.deadlineToday');
      return this.$t('tournament.deadlineLeft', { n });
    },

    /** Kategori yang biaya pendaftarannya benar-benar terisi. */
    biayaKategori() {
      return (this.turnamen.categories || []).filter((k) => Number(k.registration_fee) > 0);
    },

    /** Narahubung acara ini yang punya nomor telepon. */
    narahubung() {
      return (this.turnamen.contact_persons || []).filter((n) => n.phone);
    },

    pendaftaranMendesak() {
      const n = this.turnamen.sisa_hari_pendaftaran;
      return typeof n === 'number' && n >= 0 && n <= 7;
    },
  },

  created() {
    this.ambilDetail(this.$route.params.slug);
  },

  watch: {
    // Pindah antar turnamen lewat tautan dalam halaman tidak memuat ulang
    // komponen, sehingga datanya harus diambil ulang secara manual.
    '$route.params.slug'(baru) {
      if (baru) this.ambilDetail(baru);
    },
  },

  methods: {
    async ambilDetail(slug) {
      if (!slug) return;
      this.memuat = true;
      try {
        const { data } = await http.get(`/tournaments/detail/${slug}`);
        this.turnamen = data || {};
      } catch (error) {
        // Status 404 berarti turnamen tidak ada ATAU sedang disembunyikan.
        // Keduanya diperlakukan sama dari sisi pengunjung.
        if (error?.response?.status === 404) {
          // Aplikasi ini tidak punya halaman 404 tersendiri, jadi pengunjung
          // dikembalikan ke beranda alih-alih dibiarkan menatap halaman kosong.
          this.$toast?.info(this.$t('tournament.empty'));
          this.$router.replace('/');
          return;
        }
        this.$toast?.error(this.$t('common.errorDesc'));
      } finally {
        this.memuat = false;
      }
    },

    tanggalPanjang(nilai) {
      if (!nilai) return '';
      const d = new Date(nilai);
      if (Number.isNaN(d.getTime())) return nilai;
      return d.toLocaleDateString(this.$i18n.locale === 'en' ? 'en-GB' : 'id-ID', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
      });
    },

    rupiah(n) {
      const angka = Number(n);
      if (!Number.isFinite(angka)) return '-';
      return new Intl.NumberFormat('id-ID', {
        style: 'currency', currency: 'IDR', maximumFractionDigits: 0,
      }).format(angka);
    },

    /*
     * Nomor lokal (08xx) diubah ke bentuk internasional (628xx).
     * wa.me menolak nomor berawalan 0 dan hanya menampilkan halaman kosong —
     * tanpa pesan galat, jadi terlihat seperti tautannya yang rusak.
     */
    tautanWa(telepon) {
      const bersih = String(telepon || '').replace(/[^0-9]/g, '');
      const internasional = bersih.startsWith('0') ? `62${bersih.slice(1)}` : bersih;
      return `https://wa.me/${internasional}`;
    },

    saatKlikUnduhProposal(event) {
      if (this.mengunduh === 'loading') {
        if (event) event.preventDefault();
        return;
      }

      this.mengunduh = 'loading';
      this.$toast?.info('Memulai unduhan berkas proposal... Mohon tunggu sebentar.');

      setTimeout(() => {
        this.mengunduh = 'success';
        setTimeout(() => {
          this.mengunduh = false;
        }, 2500);
      }, 3500);
    },

    unduhDokumen() {
      if (this.urlProposal && this.urlProposal !== '#') {
        window.open(this.urlProposal, '_blank', 'noopener,noreferrer');
      }
    },
  },
};
</script>

<style scoped>
/* ── Biaya & narahubung ── */
.biaya {
  max-width: 640px;
  margin-inline: auto;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-xl);
  overflow: hidden;
  background: var(--bg-surface);
}

.biaya__baris {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-4);
  padding: var(--space-4) var(--space-5);
}

.biaya__baris + .biaya__baris { border-top: 1px solid var(--border-subtle); }
.biaya__nama { font-weight: 600; }

/* Nominal disejajarkan dengan lebar digit sama supaya terbaca sebagai
   kolom, bukan deretan angka yang bergoyang. */
.biaya__nilai {
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  color: var(--text-brand);
  white-space: nowrap;
}

.kontak-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-3);
  max-width: 760px;
  margin-inline: auto;
}

@media (min-width: 640px) { .kontak-grid { grid-template-columns: repeat(2, 1fr); } }

.kontak {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  color: var(--text-primary);
  text-decoration: none;
}

.kontak:hover { border-color: var(--accent); }

.kontak__ikon {
  display: grid;
  place-items: center;
  width: 38px; height: 38px;
  flex-shrink: 0;
  border-radius: var(--radius-full);
  background: rgb(37 211 102 / 0.14);
  color: #128c4a;
  font-size: 1.15rem;
}

.kontak__isi { display: flex; flex-direction: column; min-width: 0; }
.kontak__peran { font-size: var(--text-xs); color: var(--text-secondary); }
.kontak__telp { font-size: var(--text-xs); color: var(--text-muted); font-variant-numeric: tabular-nums; }
.kontak__panah { margin-left: auto; opacity: 0.45; font-size: 0.85rem; }

/* Warna WhatsApp dipakai apa adanya supaya tombolnya langsung dikenali.
   Teks dipaksa gelap: --text-primary bernilai terang di mode gelap, dan
   putih di atas hijau muda nyaris tidak terbaca. */
.detail__wa { background: #25d366; color: #04310f; border: 1px solid #1da851; }
.detail__wa:hover { background: #1fbe5a; color: #04310f; }

.detail__sisa { margin-left: var(--space-2); color: var(--text-brand); }
.detail__facts li.is-mendesak .detail__sisa { color: var(--danger-600); }

/*
 * Halaman ini dulu memaksa latar hitam pekat dengan teks putih, terlepas dari
 * tema yang sedang aktif, dan tombol "Download" merah polos tanpa konteks.
 * Sekarang seluruhnya memakai token sehingga ikut mode terang/gelap.
 */

/* ══ Sampul ══ */
.detail__hero {
  position: relative;
  padding-block: var(--space-8) var(--space-10);
  overflow: hidden;
}

.detail__hero-bg {
  position: absolute;
  inset: 0;
  background: var(--hero-bg);
  pointer-events: none;
}

.detail__hero-grid {
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-6);
  align-items: center;
}

/* Poster dibatasi lebarnya di ponsel; dibiarkan penuh ia setinggi 1,3 layar
   dan judul turnamen tidak pernah terlihat tanpa menggulir. */
.detail__poster {
  width: 100%;
  max-width: 210px;
  margin-inline: auto;
  border-radius: var(--radius-xl);
  overflow: hidden;
  box-shadow: var(--shadow-lg);
}

.detail__intro { text-align: center; }

.detail__title {
  font-family: var(--font-display);
  font-size: clamp(1.25rem, 5.5vw, 2.5rem);
  font-weight: 800;
  line-height: 1.15;
  color: var(--text-primary);
  margin-block: var(--space-2);
  overflow-wrap: anywhere;
}

/* ══ Fakta ringkas ══ */
.detail__facts {
  list-style: none;
  padding: 0;
  margin: 0 0 var(--space-4);
  display: grid;
  gap: var(--space-2);
  text-align: left;
}

.detail__facts li {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  padding: 8px 12px;
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
}

.detail__facts i {
  flex-shrink: 0;
  display: grid;
  place-items: center;
  width: 28px; height: 28px;
  font-size: 0.85rem;
  color: var(--accent);
  background: var(--accent-subtle);
  border-radius: var(--radius-sm);
}

.detail__facts span {
  display: flex;
  flex-direction: column;
  gap: 1px;
  font-size: 0.825rem;
  font-weight: 600;
  color: var(--text-primary);
  overflow-wrap: anywhere;
}

.detail__facts small {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
}

/* ══ Tombol ══ */
.detail__actions {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.detail__actions .ui-btn {
  justify-content: center;
  min-height: 40px;
  padding: 0.5rem 1rem;
  font-size: 0.85rem;
}

.detail__note {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  margin-top: var(--space-3);
  padding: var(--space-3) var(--space-4);
  font-size: 0.825rem;
  text-align: left;
  color: var(--text-secondary);
  background: var(--bg-sunken);
  border: 1px dashed var(--border-strong);
  border-radius: var(--radius-md);
}

.detail__note i { color: var(--accent); margin-top: 2px; }

/* ══ Keterangan ══ */
.detail__subtitle {
  font-family: var(--font-display);
  font-size: var(--text-lg);
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: var(--space-2);
}

.detail__desc {
  font-size: 0.9rem;
  line-height: 1.65;
  color: var(--text-secondary);
  white-space: pre-line;
  margin: 0;
}

/* ══ Pemuat ══ */
.loading-bar {
  position: fixed;
  top: 0; left: 0;
  width: 40%;
  height: 3px;
  background: var(--grad-flame);
  z-index: 9999;
  animation: geser-muat 1s infinite linear;
}

@keyframes geser-muat {
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(350%); }
}

.berputar { animation: putar 900ms linear infinite; }
@keyframes putar { to { transform: rotate(360deg); } }

/* ── Animasi & Status Unduh Proposal ── */
.ui-btn.is-downloading {
  cursor: wait !important;
  pointer-events: none;
  opacity: 0.85;
}

.ui-btn.is-success {
  border-color: rgba(16, 185, 129, 0.5) !important;
  color: #10b981 !important;
}

.ikon-putar {
  animation: putar 0.85s linear infinite;
  display: inline-block;
}

@keyframes pop-masuk {
  0% { transform: scale(0.6); opacity: 0; }
  60% { transform: scale(1.25); }
  100% { transform: scale(1); opacity: 1; }
}

.ikon-sukses {
  animation: pop-masuk 0.28s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
  display: inline-block;
}

@media (prefers-reduced-motion: reduce) {
  .loading-bar, .berputar { animation: none; }
}

/* ══ Layar lebih lebar ══ */
@media (max-width: 400px) { .detail__poster { max-width: 200px; } }

@media (min-width: 640px) {
  .detail__poster { max-width: 320px; }
  .detail__actions { flex-direction: row; flex-wrap: wrap; justify-content: center; }
}

@media (min-width: 992px) {
  .detail__hero { padding-block: var(--space-12) var(--space-16); }
  .detail__hero-grid { grid-template-columns: 340px 1fr; gap: var(--space-10); }
  .detail__poster { max-width: none; margin-inline: 0; }
  .detail__intro { text-align: left; }
  .detail__actions { justify-content: flex-start; }
  .detail__facts { grid-template-columns: repeat(2, 1fr); }
}
</style>
